/**
 * DeleteConfirmDialog
 *
 * MUI confirmation dialog used before deleting a portfolio artifact.
 * Replaces the native `window.confirm()` pattern with a styled MUI dialog
 * that supports an async `onConfirm` handler, an in-flight loading state,
 * and inline error surfacing when the parent's delete call rejects.
 *
 * Props:
 * - `open`: controls dialog visibility.
 * - `itemName`: name of the artifact being deleted; rendered in quotes in
 *   the default body. Falls back to "this item" when empty/whitespace.
 * - `title` (optional): overrides the default "Delete artifact?" title.
 * - `bodyText` (optional): replaces the default body entirely (and ignores
 *   `itemName`).
 * - `onClose`: called on Cancel, Escape, or backdrop click. Suppressed
 *   while a delete is in flight so the user cannot dismiss mid-operation.
 * - `onConfirm`: async handler invoked when the user confirms. The dialog
 *   shows a loading state until it settles; on resolve, calls `onClose`,
 *   on reject, keeps the dialog open and surfaces the error in an Alert.
 *
 * Generic enough to reuse for other destructive confirmations later, but
 * written for portfolio-artifacts today.
 */
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface DeleteConfirmDialogProps {
  open: boolean;
  /** The thing being deleted (e.g., the artifact title). Renders inside
   *  quotes in the body: "Delete '{itemName}'? This cannot be undone." */
  itemName: string;
  /** Optional title override. Default: "Delete artifact?" */
  title?: string;
  /** Optional body override. If supplied, replaces the default body entirely. */
  bodyText?: string;
  /** Called on Cancel or backdrop close. */
  onClose: () => void;
  /** Called when user confirms deletion. Returns a Promise so the
   *  dialog can show a loading state while the parent's delete API
   *  call is in flight. If it rejects, the dialog stays open and
   *  surfaces the error. If it resolves, the dialog closes via
   *  onClose(). */
  onConfirm: () => Promise<void>;
}

export default function DeleteConfirmDialog({
  open,
  itemName,
  title,
  bodyText,
  onClose,
  onConfirm,
}: DeleteConfirmDialogProps) {
  const { t } = useTranslation("portfolio-kit");
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolvedTitle = title ?? t("DeleteConfirmDialog.defaultTitle");
  const displayName = itemName.trim()
    ? itemName
    : t("DeleteConfirmDialog.fallbackItemName");
  const body =
    bodyText ?? t("DeleteConfirmDialog.defaultBody", { name: displayName });

  const handleClose = () => {
    if (isDeleting) return;
    setError(null);
    onClose();
  };

  const handleConfirm = async () => {
    setError(null);
    setIsDeleting(true);
    try {
      await onConfirm();
      setIsDeleting(false);
      onClose();
    } catch (err) {
      setIsDeleting(false);
      const message =
        err instanceof Error
          ? err.message
          : t("DeleteConfirmDialog.deleteFailedFallback");
      setError(message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="delete-confirm-dialog-title"
    >
      <DialogTitle id="delete-confirm-dialog-title">{resolvedTitle}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <DialogContentText>{body}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isDeleting}>
          {t("DeleteConfirmDialog.cancelButton")}
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={isDeleting}
          startIcon={
            isDeleting ? <CircularProgress size={20} color="inherit" /> : undefined
          }
        >
          {t("DeleteConfirmDialog.deleteButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
